import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchRulesMeta, fetchRules, saveRule as saveRuleApi, deleteRule as deleteRuleApi } from './service';
import {
    ACTION_VALUES,
    COLUMNS,
    CONDITION_TYPES,
    DEFAULT_CONDITION,
    DEFAULT_RULE,
    NEW_RULE_ID,
    TRANSLATION_KEY,
} from './constants';
import {
    applyConditionEdit,
    buildSavePayload,
    dedupeRuleName,
    isEditing,
    markInvalid,
    nextSortState,
    sortRules,
    validateRule,
} from './utils';

const DEFAULT_SORT_COLUMN = COLUMNS.find(column => column.key === 'lastUpdated');
const DEFAULT_SORT_DIR = 'desc';

const EMPTY_META = {
    apps: [],
    sources: [],
    countries: [],
    regions: [],
    cities: [],
    installSourceBundles: [],
    betaHiddenFlags: {},
};

const track = (eventName, params) => {
    try {
        if (typeof window !== 'undefined' && window.mixpanel) {
            window.mixpanel.track(eventName, params);
        }
    } catch (e) {
        console.error(e);
    }
};

// Stable per-condition key for React lists (conditions have no server id while editing).
let conditionKeySeq = 0;
const nextConditionKey = () => {
    conditionKeySeq += 1;
    return `c${conditionKeySeq}`;
};
const withConditionKeys = rule =>
    rule
        ? {
              ...rule,
              conditions: (rule.conditions || []).map(condition =>
                  condition.clientId ? condition : { ...condition, clientId: nextConditionKey() }
              ),
          }
        : rule;

export default function useFraudRules({ confirm, translate }) {
    const [meta, setMeta] = useState(EMPTY_META);
    const [rules, setRules] = useState([]);
    const [editing, setEditing] = useState({});
    const [searchText, setSearchText] = useState(undefined);
    const [sortKey, setSortKey] = useState(DEFAULT_SORT_COLUMN.key);
    const [sortDir, setSortDir] = useState(DEFAULT_SORT_DIR);
    const [rejectSaveDisabled, setRejectSaveDisabled] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.all([fetchRulesMeta(), fetchRules()])
            .then(([metaValue, rulesValue]) => {
                if (cancelled) return;
                setMeta({
                    apps: metaValue.apps || [],
                    sources: metaValue.sources || [],
                    countries: metaValue.countries || [],
                    regions: metaValue.regions || [],
                    cities: metaValue.cities || [],
                    installSourceBundles: metaValue.install_source_bundles || [],
                    betaHiddenFlags: metaValue.beta_hidden_flags || {},
                });
                setRules(rulesValue.rules || []);
                setRejectSaveDisabled(!!rulesValue.reject_save_disabled);
                setReadOnly(!!rulesValue.read_only);
                setLoadError(false);
            })
            .catch(error => {
                if (cancelled) return;
                console.error('Failed to load fraud custom rules', error);
                setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const conditionOptions = useMemo(() => {
        const options = { betaHiddenFlags: meta.betaHiddenFlags || {} };
        CONDITION_TYPES.forEach(type => {
            if (type.optionsKey) options[type.optionsKey] = meta[type.optionsKey];
        });
        return options;
    }, [meta]);

    const toggleEdit = useCallback(
        id => {
            setEditing(prev =>
                isEditing(prev, id)
                    ? { ...prev, [id]: undefined }
                    : { ...prev, [id]: withConditionKeys(rules.find(rule => rule.id === id)) }
            );
        },
        [rules]
    );

    const cancelEdit = useCallback(id => {
        setEditing(prev => (isEditing(prev, id) ? { ...prev, [id]: undefined } : prev));
    }, []);

    const search = useCallback(text => setSearchText(text || undefined), []);

    const onSort = useCallback(
        column => {
            const { key, dir } = nextSortState(sortKey, sortDir, column.key);
            setSortKey(key);
            setSortDir(dir);
        },
        [sortKey, sortDir]
    );

    const newRule = useCallback(
        template => {
            if (readOnly) return;
            const def = template
                ? {
                      id: NEW_RULE_ID,
                      placeUnder: template.id,
                      name: template.name,
                      action: template.action,
                      conditions: template.conditions,
                      active: true,
                      dirty: true,
                  }
                : { ...DEFAULT_RULE, conditions: [{ ...DEFAULT_CONDITION }] };
            def.name = dedupeRuleName(
                def.name,
                rules.map(rule => rule.name)
            );
            setEditing(prev => ({ ...prev, [NEW_RULE_ID]: withConditionKeys(def) }));
        },
        [readOnly, rules]
    );

    const editRule = useCallback((id, key, value) => {
        if (readOnly) return;
        setEditing(prev => ({ ...prev, [id]: { ...prev[id], [key]: value, dirty: true } }));
    }, [readOnly]);

    const addCondition = useCallback(id => {
        if (readOnly) return;
        setEditing(prev => {
            const conditions = [
                ...(prev[id]?.conditions || []),
                { ...DEFAULT_CONDITION, clientId: nextConditionKey() },
            ];
            return { ...prev, [id]: { ...prev[id], conditions, dirty: true } };
        });
    }, [readOnly]);

    const editCondition = useCallback((id, index, key, value) => {
        if (readOnly) return;
        setEditing(prev => {
            const conditions = prev[id].conditions.slice();
            conditions[index] = applyConditionEdit(conditions[index], key, value);
            return { ...prev, [id]: { ...prev[id], conditions, dirty: true } };
        });
    }, [readOnly]);

    const removeCondition = useCallback((id, index) => {
        if (readOnly) return;
        setEditing(prev => {
            const conditions = prev[id].conditions.slice();
            conditions.splice(index, 1);
            return { ...prev, [id]: { ...prev[id], conditions, dirty: true } };
        });
    }, [readOnly]);

    const doSave = useCallback(rule => {
        if (readOnly) return;
        setSaving(true);
        const payload = buildSavePayload(rule);
        saveRuleApi(payload)
            .then(value => {
                track('Fraud Rules: Update Rule', payload);
                setRules(prev =>
                    value.created ? [value.rule, ...prev] : prev.map(r => (r.id === value.rule.id ? value.rule : r))
                );
                setEditing(prev => ({ ...prev, [rule.id]: undefined }));
            })
            .catch(error => console.error('Failed to save fraud rule', error))
            .finally(() => setSaving(false));
    }, [readOnly]);

    const save = useCallback(
        id => {
            if (readOnly) return;
            const rule = editing[id];
            const valid = validateRule(rule);
            if (valid !== true) {
                setEditing(prev => ({ ...prev, [id]: markInvalid(prev[id], valid.invalids, valid.invalidName) }));
                return;
            }
            if (rule.action === ACTION_VALUES.REJECT && rule.active) {
                confirm({
                    titleKey: `${TRANSLATION_KEY}.REJECT_MODAL.TITLE`,
                    body: (
                        <span>
                            {translate(
                                `${TRANSLATION_KEY}.REJECT_MODAL.BODY`,
                                { name: rule.name },
                                { renderInnerHtml: true }
                            )}
                        </span>
                    ),
                    onConfirm: () => doSave(rule),
                });
            } else {
                doSave(rule);
            }
        },
        [readOnly, editing, confirm, translate, doSave]
    );

    const doDelete = useCallback(id => {
        if (readOnly) return;
        setDeleting(true);
        deleteRuleApi(id)
            .then(() => {
                track('Fraud Rules: Delete Rule', { id });
                setRules(prev => prev.filter(rule => rule.id !== id));
                setEditing(prev => ({ ...prev, [id]: undefined }));
            })
            .catch(error => console.error('Failed to delete fraud rule', error))
            .finally(() => setDeleting(false));
    }, [readOnly]);

    const deleteRule = useCallback(
        id => {
            if (readOnly) return;
            const rule = editing[id];
            confirm({
                titleKey: `${TRANSLATION_KEY}.DELETE_MODAL.TITLE`,
                body: (
                    <span>
                        {translate(
                            `${TRANSLATION_KEY}.DELETE_MODAL.BODY`,
                            { name: rule && rule.name },
                            { renderInnerHtml: true }
                        )}
                    </span>
                ),
                onConfirm: () => doDelete(id),
            });
        },
        [readOnly, editing, confirm, translate, doDelete]
    );

    const actions = useMemo(
        () => ({
            conditionOptions,
            toggleEdit,
            cancelEdit,
            search,
            onSort,
            newRule,
            editRule,
            addCondition,
            editCondition,
            removeCondition,
            save,
            deleteRule,
            duplicate: newRule,
        }),
        [
            conditionOptions,
            toggleEdit,
            cancelEdit,
            search,
            onSort,
            newRule,
            editRule,
            addCondition,
            editCondition,
            removeCondition,
            save,
            deleteRule,
        ]
    );

    const sortColumn = COLUMNS.find(column => column.key === sortKey);
    const sorted = sortColumn && sortDir ? sortRules(rules, sortColumn, sortDir) : rules.slice();
    const data = isEditing(editing, NEW_RULE_ID) ? [editing[NEW_RULE_ID], ...sorted] : sorted;

    return {
        meta,
        rules: data,
        editing,
        searchText,
        sortKey,
        sortDir,
        rejectSaveDisabled,
        readOnly,
        loading,
        saving,
        deleting,
        loadError,
        actions,
        newRuleOpen: isEditing(editing, NEW_RULE_ID),
    };
}
